import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { enterFullscreen, exitFullscreen } from 'src/utils/utils';

interface StateType {
  activeDir?: string | any;
  activeMode?: string; // light or dark
  activeTheme?: string;
  SidebarWidth?: number;
  MiniSidebarWidth?: number;
  TopbarHeight?: number;
  isCollapse?: boolean;
  isLayout?: string;
  isSidebarHover?: boolean;
  isMobileSidebar?: boolean;
  isHorizontal?: boolean;
  isLanguage?: string;
  isCardShadow?: boolean;
  borderRadius?: number | any;
  isFullscreen?: boolean;
  multiExpandSideMenu?: boolean;
}

const initialState: StateType = {
  activeDir: 'ltr',
  activeMode: 'light', // light or dark
  activeTheme: 'SECUSEEK_THEME', // SECUSEEK_THEME, BLUE_THEME, GREEN_THEME, BLACK_THEME, PURPLE_THEME, ORANGE_THEME
  SidebarWidth: 300,
  MiniSidebarWidth: 77,
  TopbarHeight: 70,
  isLayout: 'full', // full or boxed
  isCollapse: JSON.parse(window.localStorage.getItem('isCollapse') || 'false'), // to make sidebar Mini by default
  isSidebarHover: false,
  isMobileSidebar: false,
  isHorizontal: false,
  isLanguage: 'en',
  isCardShadow: true,
  borderRadius: 7,
  isFullscreen: false,
  multiExpandSideMenu: true,
};

export const CustomizerSlice = createSlice({
  name: 'customizer',
  initialState,
  reducers: {
    setTheme: (state: StateType, action) => {
      state.activeTheme = action.payload;
    },
    setDarkMode: (state: StateType, action) => {
      state.activeMode = action.payload;
      window?.localStorage?.setItem('theme', action.payload.toString());
    },
    setLanguage: (state: StateType, action) => {
      state.isLanguage = action.payload;
    },
    setCardShadow: (state: StateType, action) => {
      state.isCardShadow = action.payload;
    },
    toggleSidebar: (state) => {
      state.isCollapse = !state.isCollapse;
      window?.localStorage?.setItem('isCollapse', JSON.stringify(state.isCollapse));
    },
    hoverSidebar: (state: StateType, action) => {
      state.isSidebarHover = action.payload;
    },
    toggleMobileSidebar: {
      reducer: (state, action: { payload?: boolean }) => {
        if (action.payload !== undefined) {
          state.isMobileSidebar = Boolean(action.payload);
        } else {
          state.isMobileSidebar = !state.isMobileSidebar;
        }
      },
      prepare: (payload?: boolean) => ({ payload }),
    },
    toggleLayout: (state: StateType, action) => {
      state.isLayout = action.payload;
    },
    toggleHorizontal: (state: StateType, action) => {
      state.isHorizontal = action.payload;
    },
    setBorderRadius: (state: StateType, action) => {
      state.borderRadius = action.payload;
    },
    setFullscreen: (state, action) => {
      state.isFullscreen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(toggleFullscreen.fulfilled, (state: any, action: any) => {
      state.isFullscreen = action.payload;
    });
  },
});

export const toggleFullscreen = createAsyncThunk('customizer/isFullScreen', async (_, { getState }) => {
  const state = getState() as any;
  const { isFullscreen } = state.customizer;

  if (isFullscreen) {
    exitFullscreen();
  } else {
    enterFullscreen();
  }
  return !isFullscreen;
});

export const { setTheme, setDarkMode, toggleSidebar, hoverSidebar, toggleMobileSidebar, toggleLayout, setBorderRadius, toggleHorizontal, setLanguage, setCardShadow } = CustomizerSlice.actions;

export default CustomizerSlice.reducer;
